import { useEffect, useState } from 'react';
import { fetchAicaFinancialMetrics, FinancialMetricsData, FinancialMetricsHeader, FinancialMetricsRow } from './financialMetricsApi';
import { computeDelta, DisplayMetric, formatMetricValue, KPI_METRIC_CONFIG, MetricTrend, SPARKLINE_METRIC_CONFIG } from './constants';
import { HEADER_TYPE } from '../../../../../../store/dashboard/auditedFinancials/constants';
import { getCurrentFinancialYearEndYear } from '../../../../../../utils/dateUtils';
import { isAnnualisedPartialHeader, getAnnualisedFyLabel } from '../FinancialMetrics/annualisedHeader';

interface UseFinancialMetricsResult {
    loading: boolean;
    fyLabel: string;
    kpiMetrics: DisplayMetric[];
    sparklineMetrics: DisplayMetric[];
}

const FLAT_TREND_THRESHOLD = 0.05;
const TRAILING_TWO_DIGITS = /(\d{2})$/;

export const extractEndYear = (name: string) => name.match(TRAILING_TWO_DIGITS)?.[1];

export const getYearHeaders = (data: FinancialMetricsData) => data.headers.filter((h) => h.type);

export const headerEndYear = (name?: string): number | null => {
    const yy = name ? extractEndYear(name) : undefined;
    return yy ? 2000 + parseInt(yy, 10) : null;
};

/* 
   Picks the headline column: the latest financial year that is NOT a future
   projection. Audited years (always historical) are all eligible; provisional
   columns are eligible only up to and including the current financial year — so
   the current-year provisional shows, but forward-looking projected provisionals
   (e.g. FY30-31) are dropped. When the chosen FY has both an audited and a
   provisional column, audited wins. Returns -1 when nothing qualifies. 
*/
export const selectDisplayHeaderIndex = (yearHeaders: { name?: string; type?: string }[], currentFyEndYear: number): number => {
    let selected = -1;
    yearHeaders.forEach((header, index) => {
        const endYear = headerEndYear(header.name);
        const isProjectedProvisional = header.type === HEADER_TYPE.PROVISIONAL_FINANCIALS && endYear !== null && endYear > currentFyEndYear;
        if (!isProjectedProvisional) selected = index;
    });
    if (selected === -1) return selected;

    // Same FY, both audited & provisional present → prefer the audited column.
    const selectedHeader = yearHeaders[selected];
    const selectedYear = headerEndYear(selectedHeader.name);
    if (selectedHeader.type === HEADER_TYPE.PROVISIONAL_FINANCIALS && selectedYear !== null) {
        const auditedSameFy = yearHeaders.findIndex(
            (header) => header.type === HEADER_TYPE.AUDITED_FINANCIALS && headerEndYear(header.name) === selectedYear,
        );
        if (auditedSameFy !== -1) selected = auditedSameFy;
    }
    return selected;
};

/* 
   Header FY label as a financial-year range (e.g. FY'26-27), derived from the
   same column the metric values are read from. The start year is the end year
   minus one. Falls back to the raw header name when no trailing year digits are
   present.
*/
export const buildFyLabel = (lastHeader?: Pick<FinancialMetricsHeader, 'name'> & Partial<FinancialMetricsHeader>): string => {
    if (lastHeader && isAnnualisedPartialHeader(lastHeader)) return getAnnualisedFyLabel(lastHeader);
    const endYear = lastHeader ? extractEndYear(lastHeader.name) : null;
    if (!endYear) return lastHeader?.name ?? '';
    const startYear = String((parseInt(endYear, 10) + 99) % 100).padStart(2, '0');
    return `FY'${startYear}-${endYear}`;
};

export const buildSparklineFromYears = (
    row: FinancialMetricsRow,
    yearHeaders: { key: string }[],
): { points: { value: number }[]; trend: MetricTrend; delta: string | null } => {
    const raw = yearHeaders.map((h) => row[h.key]).filter((v): v is number => typeof v === 'number');
    if (raw.length < 2) return { points: [{ value: 0.5 }, { value: 0.5 }], trend: 'flat', delta: null };
    const min = Math.min(...raw);
    const max = Math.max(...raw);
    const range = max - min;
    const first = raw[0];
    const last = raw[raw.length - 1];
    if (range === 0) return { points: raw.map(() => ({ value: 0.5 })), trend: 'flat', delta: '0.0%' };
    const trend: MetricTrend = Math.abs(last - first) / range < FLAT_TREND_THRESHOLD ? 'flat' : last > first ? 'up' : 'down';
    const { delta } = computeDelta(last, first);
    return { points: raw.map((v) => ({ value: (v - min) / range })), trend, delta };
};

const toDisplayMetric = (
    config: { metricName: string; label: string },
    data: FinancialMetricsData,
    currentKey: string,
    periodLabel: string,
    yearHeaders: { key: string }[],
): DisplayMetric | null => {
    const row = data.rows.find((r) => r.metricName === config.metricName);
    if (!row) return null;

    const current = row[currentKey] as number | null | undefined;
    const sparkline = buildSparklineFromYears(row, yearHeaders);

    return {
        key: config.metricName,
        label: config.label,
        value: formatMetricValue(current, row.unit),
        delta: sparkline.delta,
        trend: sparkline.trend,
        sparkline: sparkline.points,
        periodLabel,
    };
};

export function useFinancialMetrics(orgId?: string): UseFinancialMetricsResult {
    const [data, setData] = useState<FinancialMetricsData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orgId) return;
        let active = true;
        setLoading(true);
        fetchAicaFinancialMetrics(orgId)
            .then((result) => active && setData(result))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [orgId]);

    if (!data) {
        return { loading, fyLabel: '', kpiMetrics: [], sparklineMetrics: [] };
    }

    const yearHeaders = getYearHeaders(data);
    const displayIndex = selectDisplayHeaderIndex(yearHeaders, getCurrentFinancialYearEndYear());
    const displayHeader = displayIndex >= 0 ? yearHeaders[displayIndex] : undefined;
    const trendHeaders = displayIndex >= 0 ? yearHeaders.slice(0, displayIndex + 1) : [];
    const currentKey = displayHeader?.key ?? '';

    const fyLabel = buildFyLabel(displayHeader);
    const periodLabel = fyLabel;

    const transform = (configs: { metricName: string; label: string }[]) =>
        configs
            .map((config) => toDisplayMetric(config, data, currentKey, periodLabel, trendHeaders))
            .filter((m): m is DisplayMetric => m !== null);

    return {
        loading,
        fyLabel,
        kpiMetrics: transform(KPI_METRIC_CONFIG),
        sparklineMetrics: transform(SPARKLINE_METRIC_CONFIG),
    };
}
